/**
 * Pointer gestures of the galaxy map as a small state machine (pure: no DOM, no Pixi, unit-tested).
 *
 *   one pointer    pan (a short, still touch is a tap)
 *   two pointers   pinch: the world point under the midpoint of the fingers stays under it (zoom + two-finger pan)
 *   back to one    pan continues with the remaining finger, without a jump and without a tap
 *   cancel         pointercancel / lost capture / window blur: every pointer is forgotten, the next gesture is clean
 *
 * Tracking starts at pointerdown (not at the first move), so the first gesture works like every later one.
 */

export interface Camera { x: number; y: number; scale: number }
export interface Pt { x: number; y: number }

export const TAP_SLOP_PX = 7;
export const TAP_MS = 600;
/** Fingers closer than this are treated as this far apart: a pinch that starts on two touching fingers must not explode. */
export const MIN_PINCH_DIST_PX = 24;

export function clampScale(scale: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, scale));
}

/** Zooms to `scale`, keeping the world point under screen point `p` where it is. */
export function zoomAt(cam: Camera, p: Pt, scale: number, min: number, max: number): Camera {
  const s = clampScale(scale, min, max);
  const wx = (p.x - cam.x) / cam.scale, wy = (p.y - cam.y) / cam.scale;
  return { x: p.x - wx * s, y: p.y - wy * s, scale: s };
}

export function midpoint(a: Pt, b: Pt): Pt { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }
export function distance(a: Pt, b: Pt): number { return Math.hypot(a.x - b.x, a.y - b.y); }

export interface PinchStart { dist: number; scale: number; anchor: Pt /* world point under the midpoint */ }

export function startPinch(cam: Camera, a: Pt, b: Pt): PinchStart {
  const m = midpoint(a, b);
  return {
    dist: Math.max(distance(a, b), MIN_PINCH_DIST_PX),
    scale: cam.scale,
    anchor: { x: (m.x - cam.x) / cam.scale, y: (m.y - cam.y) / cam.scale },
  };
}

/** Camera for the fingers at `a` and `b`: scale follows the finger distance, the anchor follows the midpoint. */
export function pinchCamera(start: PinchStart, a: Pt, b: Pt, min: number, max: number): Camera {
  const scale = clampScale(start.scale * (Math.max(distance(a, b), MIN_PINCH_DIST_PX) / start.dist), min, max);
  const m = midpoint(a, b);
  return { x: m.x - start.anchor.x * scale, y: m.y - start.anchor.y * scale, scale };
}

export interface GestureHost {
  getCamera(): Camera;
  setCamera(cam: Camera): void;
  onTap(p: Pt): void;
  /** true while a finger/mouse is down: the renderer postpones its heavy rebuilds to the end of the gesture */
  onActiveChange?(active: boolean): void;
  minScale: number;
  maxScale: number;
}

interface Tracked { x: number; y: number }

export class CameraGestures {
  private pointers = new Map<number, Tracked>();
  private pinch: PinchStart | null = null;
  private last: Pt = { x: 0, y: 0 };
  private down = { x: 0, y: 0, t: 0 };
  private moved = false;
  /** a second finger was involved: the gesture can no longer end in a tap */
  private multi = false;

  constructor(private host: GestureHost) {}

  get active(): boolean { return this.pointers.size > 0; }
  get pointerCount(): number { return this.pointers.size; }
  get pinching(): boolean { return this.pinch !== null; }

  pointerDown(id: number, p: Pt, now: number): void {
    const wasActive = this.active;
    if (this.pointers.has(id)) this.pointers.delete(id); // a missed up/cancel: start over for this id
    this.pointers.set(id, { x: p.x, y: p.y });
    if (this.pointers.size === 1) {
      this.moved = false;
      this.multi = false;
      this.down = { x: p.x, y: p.y, t: now };
      this.last = { x: p.x, y: p.y };
    } else {
      this.multi = true;
      this.moved = true;
      if (this.pointers.size === 2) this.beginPinch();
    }
    if (!wasActive) this.host.onActiveChange?.(true);
  }

  pointerMove(id: number, p: Pt): void {
    const t = this.pointers.get(id);
    if (!t) return; // hover (mouse without a button) or a pointer we do not track
    t.x = p.x; t.y = p.y;
    if (this.pinch) {
      const [a, b] = this.firstTwo();
      this.host.setCamera(pinchCamera(this.pinch, a, b, this.host.minScale, this.host.maxScale));
      return;
    }
    if (this.pointers.size !== 1) return;
    if (!this.moved && distance(p, this.down) > TAP_SLOP_PX) this.moved = true;
    if (!this.moved) return;
    const cam = this.host.getCamera();
    this.host.setCamera({ x: cam.x + p.x - this.last.x, y: cam.y + p.y - this.last.y, scale: cam.scale });
    this.last = { x: p.x, y: p.y };
  }

  pointerUp(id: number, p: Pt, now: number): void {
    if (!this.pointers.has(id)) return;
    const wasTap = this.pointers.size === 1 && !this.moved && !this.multi && now - this.down.t < TAP_MS;
    this.pointers.delete(id);
    this.pinch = null;
    if (this.pointers.size >= 2) this.beginPinch(); // a third finger lifted: the first two carry on
    else if (this.pointers.size === 1) {
      const rest = [...this.pointers.values()][0]!;
      this.last = { x: rest.x, y: rest.y }; // pan on from where the remaining finger is: no jump
      this.moved = true;
    } else this.host.onActiveChange?.(false);
    if (wasTap) this.host.onTap(p);
  }

  /** pointercancel, lost pointer capture, window blur: forget everything */
  cancel(): void {
    const was = this.active;
    this.pointers.clear();
    this.pinch = null;
    this.moved = false;
    this.multi = false;
    if (was) this.host.onActiveChange?.(false);
  }

  private firstTwo(): [Pt, Pt] {
    const it = this.pointers.values();
    return [it.next().value as Tracked, it.next().value as Tracked];
  }

  private beginPinch(): void {
    const [a, b] = this.firstTwo();
    this.pinch = startPinch(this.host.getCamera(), a, b);
  }
}
